import React from 'react';
import { Text, View, ScrollView, StyleProp, ViewStyle } from 'react-native';
import { connect } from 'react-redux';
import i18n from 'i18n-js';

import ColorWrapper from '@styles/colors';
import { fontOP as initialFont } from '@styles/fonts';

import { fullHeight, fullWidth, scaleOP as initialScale } from '@components/frame';
import { ColorFunction } from '@styles/colors/types';
import {
  isWeb,
  isPhone,
  LEVELS,
  searchDisplayMode,
  ALL_MACHINES,
} from '../../../../helpers';
import CloseButton from '@components/closeButton';
import { Machine } from '@interfaces/machines';
import { getMachines, setSelectedMachine } from '@reducers/machines';
import { getBoxShadowStyle } from '@styles/shadow';
import { BOTTOM_SHEET_TOP_OFFSET, SCREEN_BORDER_RADIUS, NOT_PANEL_VIEW_MODE_WIDTH, TABLE_NAMES, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import Search from '@components/search';

import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { OperationSearchItem } from '@interfaces/search';
import { setScreenToBackFromJobPerspective } from '@reducers/operatorPanel';
import Selectors from '@styles/selectors';
import { LevelSubScreen } from '@v2/pages/common/enums';
import { TourTarget } from '@components/tours';
import { WELCOME_TOUR_IDS } from '@components/tours/config/global/welcome/constants';
import { TableName } from '@interfaces/base';
import { useManageOperatorPanelScheduled } from './hooks/useManageOperatorPanelScheduled';

interface OperatorPanelScheduledProps {
  C: ColorFunction;
  font?: typeof initialFont;
  selectedItem?: Machine;
  getMachines: (formatResult?: boolean, preselectedId?: number) => Promise<Machine[]>;
  setSelectedMachine: any;
  machines: Machine[];
  statStorage: any;
  scale?: ScaleFunction;
  setScreenToBackFromJobPerspective: (params: Record<string, unknown>) => void;
  bottomSheetLayout?: boolean;
  containerWidth?: number;
  customBg?: string;
  withoutCloseButton?: boolean;
  withoutHeaderTitle?: boolean;
  headerStyle?: StyleProp<ViewStyle>;
  shouldTrackSelectedMachine?: boolean;
  onScheduledJobPress?: (job: OperationSearchItem, itemType: string, levelSubScreen: LevelSubScreen) => void;
  jobMainAppTileView?: boolean;
  searchInputViewSelectorBg?: string;
  searchInputWithModeSelectorStyle?: StyleProp<ViewStyle>;
  showModeSelectorInPopupMode?: boolean;
  shouldUseDefaultHorizontalPaddings?: boolean;
  tableName: TableName;
}

const OperatorPanelScheduled: React.FC<OperatorPanelScheduledProps> = ({
  C,
  getMachines,
  setScreenToBackFromJobPerspective,
  selectedItem: selectedMachine,
  setSelectedMachine,
  customBg,
  headerStyle,
  machines,
  onScheduledJobPress,
  searchInputViewSelectorBg,
  searchInputWithModeSelectorStyle,
  font = initialFont,
  scale = initialScale,
  shouldUseDefaultHorizontalPaddings = false,
  bottomSheetLayout = true,
  withoutCloseButton = false,
  withoutHeaderTitle = false,
  shouldTrackSelectedMachine = false,
  jobMainAppTileView = false,
  showModeSelectorInPopupMode = true,
  containerWidth = fullWidth,
  statStorage = {},
  tableName = TABLE_NAMES.operatorPanelScheduled,
}) => {
  const { OPERATIONS_AFTER_NEXT_WEEK, OPERATIONS_LATE, OPERATIONS_NEXT_WEEK, OPERATIONS_NOW } = statStorage || {};

  const {
    filterOptions,
    oDataFilter,
    selectedFilter,
    selectedFilterObject,
    handleOperationSelect,
    handleFilterClick,
    onClose,
    mainBg,
    statusBarBg,
    shouldDisplayFilterSelectors,
  } = useManageOperatorPanelScheduled({
    C,
    selectedMachine,
    machines,
    getMachines,
    setSelectedMachine,
    setScreenToBackFromJobPerspective,
    shouldTrackSelectedMachine,
    onScheduledJobPress,
    customBg,
  });

  return (
    <>
      {isWeb && bottomSheetLayout ? (
        <View
          style={{
            position: 'absolute',
            top: 0,
            width: '100%',
            height: scale(44),
            backgroundColor: statusBarBg,
          }}
        />
      ) : null}
      <View
        style={[
          bottomSheetLayout && {
            backgroundColor: mainBg,
            height: fullHeight - (isWeb ? scale(12) : BOTTOM_SHEET_TOP_OFFSET / 2),
            position: 'absolute',
            top: isWeb ? scale(12) : BOTTOM_SHEET_TOP_OFFSET / 2,
            flexDirection: 'column',
            borderTopLeftRadius: scale(SCREEN_BORDER_RADIUS),
            borderTopRightRadius: scale(SCREEN_BORDER_RADIUS),
            overflow: 'hidden',
          },
          isWeb && bottomSheetLayout && getBoxShadowStyle({ type: 'Ultra Wide' }),
          { width: containerWidth, maxWidth: containerWidth, flex: 1, },
        ]}
      >
        {/* Header Section - All in one line */}
        <TourTarget
          tourId={WELCOME_TOUR_IDS.OP_SCHEDULED_OVERVIEW}
          style={[
            {
              width: '100%',
              paddingTop: scale(18),
              paddingBottom: scale(9),
              paddingLeft: scale(24),
              paddingRight: scale(22),
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              zIndex: 10,
              backgroundColor: mainBg,
            },
            headerStyle,
          ]}
        >
          {/* Title */}
          {!withoutHeaderTitle && (
            <View
              style={{
                width: scale(200),
              }}
            >
              <Text
                style={[
                  font['Footnote'],
                  {
                    color: C('Label/Light/Quaternary'),
                    position: 'absolute',
                    top: scale(-14),
                  },
                ]}
              >
                {`${OPERATIONS_AFTER_NEXT_WEEK + OPERATIONS_LATE + OPERATIONS_NEXT_WEEK + OPERATIONS_NOW}`} {i18n.t('Unfinished')}
              </Text>
              <Text
                style={[
                  font['Headline Large'],
                  {
                    color: C('Label/Light/Primary'),
                  },
                ]}
              >
                {i18n.t('Scheduled Jobs')}
              </Text>
            </View>
          )}

          {/* Filter Buttons - Centered */}
          {shouldDisplayFilterSelectors && (
            <View
              style={{
                ...(((isWeb && containerWidth > NOT_PANEL_VIEW_MODE_WIDTH) || (!isWeb && !isPhone)) && bottomSheetLayout
                  ? {
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      ...(!isWeb && { top: scale(15) }),
                    }
                  : {
                      flex: 4,
                    }),
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <ScrollView
                horizontal={true}
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={{
                  paddingHorizontal: scale(18),
                  justifyContent: 'center',
                  flexGrow: 1,
                }}
              >
                <View
                  style={{
                    flex: 2,
                    flexDirection: 'row',
                    gap: scale(8),
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {filterOptions.map((filter) => {
                    const filterTestIds: Record<string, string> = {
                      machine: TEST_IDS.scheduledScreen.filterSelectorMachine,
                      operation: TEST_IDS.scheduledScreen.filterSelectorMatchedOps,
                      all: TEST_IDS.scheduledScreen.filterSelectorAllOps,
                    };
                    return (
                    <Selectors
                      key={`${filter.type}-${filter.id}-${filter.name}`}
                      {...{
                        scale,
                        font,
                        selected: selectedFilter === filter.name,
                        variant: 'L',
                        text: i18n.t(filter.name),
                        onPress: () => handleFilterClick(filter),
                        testID: filterTestIds[filter.type],
                      }}
                    />
                  );
                  })}
                </View>
              </ScrollView>
            </View>
          )}

          {/* Close Button */}
          {!withoutCloseButton && (
            <View
              style={{
                width: scale(200),
                alignItems: 'flex-end',
                position: 'absolute',
                top: scale(12),
                right: scale(22),
              }}
            >
              <CloseButton onPress={onClose} C={C} scale={scale} />
            </View>
          )}
        </TourTarget>

        {/* Search Component - Operations only */}
        <View style={{ flex: 1, width: '100%' }}>
          <Search
            key={`search-${selectedFilterObject?.type}-${selectedFilterObject?.id}`}
            isPopupMode={true}
            popupWidth={containerWidth}
            tableNamePrefix={tableName}
            route={{
              params: {
                ignoreModeHistory: true,
                displayMode: searchDisplayMode.infoTiles,
                level: LEVELS.Operations,
                oDataFilter: oDataFilter,
                search: '',
              },
            }}
            defaultOrderBy={['ScheduleExceedsOn asc']}
            onItemSelect={handleOperationSelect}
            customBackgroundColor={C(customBg ?? 'Background/Light/Panel')}
            hideLevelNavigation={true}
            hideScrollHeader={true}
            disabledModes={isWeb ? [searchDisplayMode.tableCompact] : [searchDisplayMode.line]}
            scale={scale}
            font={font}
            jobMainAppTileView={jobMainAppTileView}
            searchInputViewSelectorBg={searchInputViewSelectorBg}
            showModeSelectorInPopupMode={showModeSelectorInPopupMode}
            searchInputWithModeSelectorStyle={searchInputWithModeSelectorStyle}
            shouldUseDefaultHorizontalPaddings={shouldUseDefaultHorizontalPaddings}
            tableName={tableName}
          />
        </View>
      </View>
    </>
  );
};

export default connect(
  (state: any) => ({
    machines: state.machines?.machines,
    selectedItem: state.machines?.selectedItem,
    statStorage: state.search?.statStorage,
  }),
  { setSelectedMachine, getMachines, setScreenToBackFromJobPerspective }
)(ColorWrapper(OperatorPanelScheduled));
